<template>
	<v-card 
		outlined 
		style = "width: 100%; height: 100%; object-fit: fill; max-width: 100%;" 
		class = "pb-0 vue-draggable-handle mx-auto"
		:ref   = "'card_relay_' + layout_id"
	>
		<v-card-title
			class = "pa-2 caption secondary white--text"
		>
			<v-icon
				x-small
				color  = "primary"
				class  = "px-2"
				v-text = "'mdi-switch'"
			></v-icon>
			<span v-text="$ml.get('relay_status') || 'Relay Status'"></span>
			<v-spacer></v-spacer>
			<v-chip
				x-small
				:color = "relay_data ? 'success' : 'grey'"
				text-color = "white"
			>
				{{ relay_data ? 'Connected' : 'Disconnected' }}
			</v-chip>
		</v-card-title>
		<v-card-text
			class = "pa-2"
			style = "overflow-y: auto; max-height: calc(100% - 80px);"
		>
			<template v-if="relay_data">
				<!-- Información del Relay -->
				<v-row dense class="mb-2" justify="center">
					<v-col cols="12" class="text-center">
						<v-chip x-small color="info" text-color="white" class="mr-1 mb-1">
							Serial: {{ relay_data.serial }}
						</v-chip>
						<v-chip x-small color="info" text-color="white" class="mb-1">
							IP: {{ relay_data.ip }}
						</v-chip>
					</v-col>
				</v-row>

				<!-- Relays Section -->
				<v-row dense class="mb-3">
					<v-col cols="12">
						<div class="text-caption font-weight-bold mb-2 text-center">
							{{ $ml.get('relays') || 'Relays' }} ({{ relay_data.relays ? relay_data.relays.count : 0 }})
						</div>
						<v-row dense justify="center">
							<v-col
								v-for="relayNum in (relay_data.relays ? relay_data.relays.list : [])"
								:key="'relay-' + relayNum"
								cols="12"
								sm="6"
								md="4"
								lg="3"
								xl="2"
								class="d-flex justify-center"
							>
								<v-card
									:class="[
										'relay-card',
										getRelayState(relayNum) ? 'relay-on' : 'relay-off',
										getRelayState(relayNum) !== previousRelayStates['relay' + relayNum] ? 'state-changed' : ''
									]"
									elevation="2"
									@click="toggleRelay(relayNum)"
									:style="togglingRelays['relay' + relayNum] ? { opacity: 0.6, pointerEvents: 'none' } : {}"
									class="mx-auto"
									style="width: 100%;"
								>
									<v-card-text class="pa-2 text-center">
										<v-progress-circular
											v-if="togglingRelays['relay' + relayNum]"
											indeterminate
											color="primary"
											size="24"
											width="2"
											class="mb-1"
										></v-progress-circular>
										<v-icon
											v-else
											:color="getRelayState(relayNum) ? 'success' : 'grey'"
											size="32"
											class="mb-1"
										>
											{{ getRelayState(relayNum) ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off' }}
										</v-icon>
										<div class="text-caption font-weight-bold">
											{{ $ml.get('relay') || 'Relay' }} {{ relayNum }}
										</div>
										<div 
											:class="[
												'text-caption mt-1',
												getRelayState(relayNum) ? 'success--text' : 'grey--text'
											]"
										>
											{{ getRelayState(relayNum) ? 'ON' : 'OFF' }}
										</div>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>
					</v-col>
				</v-row>

				<!-- Inputs Section -->
				<v-row dense>
					<v-col cols="12">
						<div class="text-caption font-weight-bold mb-2 text-center">
							{{ $ml.get('inputs') || 'Inputs' }} ({{ relay_data.inputs ? relay_data.inputs.count : 0 }})
						</div>
						<v-row dense justify="center">
							<v-col
								v-for="inputNum in (relay_data.inputs ? relay_data.inputs.list : [])"
								:key="'input-' + inputNum"
								cols="12"
								sm="6"
								md="4"
								lg="3"
								xl="2"
								class="d-flex justify-center"
							>
								<v-card
									:class="[
										'input-card',
										getInputState(inputNum) ? 'input-on' : 'input-off',
										getInputState(inputNum) !== previousInputStates['input' + inputNum] ? 'state-changed' : ''
									]"
									elevation="2"
									@click="toggleInput(inputNum)"
									:style="togglingInputs['input' + inputNum] ? { opacity: 0.6, pointerEvents: 'none' } : {}"
									class="mx-auto"
									style="width: 100%;"
								>
									<v-card-text class="pa-2 text-center">
										<v-progress-circular
											v-if="togglingInputs['input' + inputNum]"
											indeterminate
											color="primary"
											size="24"
											width="2"
											class="mb-1"
										></v-progress-circular>
										<v-icon
											v-else
											:color="getInputState(inputNum) ? 'primary' : 'grey'"
											size="32"
											class="mb-1"
										>
											{{ getInputState(inputNum) ? 'mdi-circle' : 'mdi-circle-outline' }}
										</v-icon>
										<div class="text-caption font-weight-bold">
											{{ $ml.get('input') || 'Input' }} {{ inputNum }}
										</div>
										<div 
											:class="[
												'text-caption mt-1',
												getInputState(inputNum) ? 'primary--text' : 'grey--text'
											]"
										>
											{{ getInputState(inputNum) ? 'ACTIVE' : 'INACTIVE' }}
										</div>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
			</template>
			<template v-else>
				<v-row>
					<v-col cols="12" class="text-center">
						<v-progress-circular
							indeterminate
							color="primary"
							size="48"
							class="mb-2"
						></v-progress-circular>
						<div class="text-caption grey--text">
							{{ $ml.get('waiting_relay_data') || 'Waiting for relay data...' }}
						</div>
					</v-col>
				</v-row>
			</template>
		</v-card-text>
	</v-card>
</template>

<style scoped>
.relay-card,
.input-card {
	transition: all 0.3s ease;
	cursor: pointer;
	border: 2px solid transparent;
	min-width: 120px;
}

.relay-card:hover,
.input-card:hover {
	transform: scale(1.05);
}

.relay-on {
	background-color: rgba(76, 175, 80, 0.1) !important;
	border-color: #4caf50 !important;
}

.relay-off {
	background-color: rgba(158, 158, 158, 0.1) !important;
	border-color: #9e9e9e !important;
}

.input-on {
	background-color: rgba(33, 150, 243, 0.1) !important;
	border-color: #2196f3 !important;
}

.input-off {
	background-color: rgba(158, 158, 158, 0.1) !important;
	border-color: #9e9e9e !important;
}

.state-changed {
	animation: pulse 0.6s ease-in-out;
	box-shadow: 0 0 15px rgba(33, 150, 243, 0.5) !important;
}

@keyframes pulse {
	0% {
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
	}
	50% {
		transform: scale(1.05);
		box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
	}
	100% {
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
	}
}
</style>

<script>
import EventBus from '@/event-bus'

export default {
	name: 'relay_status',
	props: [
		'layout_id',
		'info_device',
		'hosts',
	],
	data() {
		return {
			relay_data: null,
			device_serial: null,
			previousRelayStates: {},
			previousInputStates: {},
			pollingInterval: null, // Intervalo para actualización periódica
			togglingRelays: {}, // Rastrear relays que están siendo cambiados
			togglingInputs: {}, // Rastrear inputs que están siendo cambiados
		}
	},
	created() {
		// Obtener el serial del dispositivo desde info_device o hosts
		this.updateSerial();
	},
	mounted() {
		// Escuchar eventos de relay_info
		EventBus.$on('relay_info', this.handleRelayInfo);
		// Hacer request HTTP inicial para obtener datos del relay
		this.fetchRelayInfo();
		// Configurar actualización periódica cada 5 segundos (5000ms)
		//this.startPolling();
	},
	beforeDestroy() {
		// Limpiar listener
		EventBus.$off('relay_info', this.handleRelayInfo);
		// Limpiar intervalo de polling
		this.stopPolling();
	},
	watch: {
		info_device: {
			handler() {
				this.updateSerial();
			},
			immediate: true,
			deep: true
		},
		hosts: {
			handler() {
				this.updateSerial();
			},
			immediate: true,
			deep: true
		}
	},
	methods: {
		updateSerial() {
			// Intentar obtener el serial de diferentes fuentes
			if (this.info_device && this.info_device.serial) {
				this.device_serial = String(this.info_device.serial);
			} else if (this.hosts && this.hosts.serial) {
				this.device_serial = String(this.hosts.serial);
			} else if (this.hosts && this.hosts.info_cam && this.hosts.info_cam.serial) {
				this.device_serial = String(this.hosts.info_cam.serial);
			} else if (this.info_device && this.info_device.info_cam && this.info_device.info_cam.serial) {
				this.device_serial = String(this.info_device.info_cam.serial);
			}
			
		},
		async fetchRelayInfo() {
			try {
				// Asegurarse de que tenemos el serial antes de hacer el request
				if (!this.device_serial) {
					this.updateSerial();
					// Esperar un momento para que se actualice el serial
					await new Promise(resolve => setTimeout(resolve, 100));
				}

				if (!this.device_serial) {
					return;
				}

				const api = this.$URL + "/api/v1/mqtt/relays/info?timeout=2";
				const res = await this.$axios.get(api);
				
				// Procesar la respuesta
				if (res && res.data && res.data.success && res.data.data && res.data.data.peripherals) {
					this.handleRelayInfo(res.data);
				}
			} catch (er) {
				// Error al obtener información del relay
			}
		},
		handleRelayInfo(message) {
			if (!message || !message.success || !message.data || !message.data.peripherals) {
				return;
			}

			if (!this.device_serial) {
				this.updateSerial();
			}

			// Buscar el periférico que coincida con el serial del dispositivo
			const peripheral = message.data.peripherals.find(p => {
				const pSerial = String(p.serial || '');
				const deviceSerial = String(this.device_serial || '');
				return pSerial === deviceSerial;
			});

			if (peripheral) {
				// Guardar estados anteriores para detectar cambios
				if (this.relay_data && this.relay_data.relays) {
					this.relay_data.relays.list.forEach(num => {
						this.previousRelayStates['relay' + num] = this.getRelayState(num);
					});
				}
				if (this.relay_data && this.relay_data.inputs) {
					this.relay_data.inputs.list.forEach(num => {
						this.previousInputStates['input' + num] = this.getInputState(num);
					});
				}

				// Actualizar datos del relay
				this.relay_data = { ...peripheral };

				// Limpiar clases de animación después de un tiempo
				this.$nextTick(() => {
					setTimeout(() => {
						// Las clases se limpiarán automáticamente en el próximo ciclo
					}, 600);
				});
			}
		},
		getRelayState(relayNum) {
			if (!this.relay_data || !this.relay_data.relays || !this.relay_data.relays.states) {
				return false;
			}
			return this.relay_data.relays.states['relay' + relayNum] === true;
		},
		getInputState(inputNum) {
			if (!this.relay_data || !this.relay_data.inputs || !this.relay_data.inputs.states) {
				return false;
			}
			const inputKey = 'input' + inputNum;
			const state = this.relay_data.inputs.states[inputKey];
			
			// Manejar diferentes formatos de estado
			if (state === undefined || state === null) {
				return false;
			}
			// Si es booleano true, está activo
			if (state === true) {
				return true;
			}
			// Si es string 'true', está activo
			if (String(state).toLowerCase() === 'true') {
				return true;
			}
			// Si es número 1, está activo
			if (Number(state) === 1) {
				return true;
			}
			return false;
		},
		async toggleRelay(relayNum) {
			// Validar que tenemos el serial del dispositivo
			if (!this.device_serial) {
				this.updateSerial();
				if (!this.device_serial) {
					return;
				}
			}

			// Evitar múltiples clicks mientras se procesa
			const relayKey = 'relay' + relayNum;
			if (this.togglingRelays[relayKey]) {
				return;
			}

			// Obtener el estado actual del relay
			const currentState = this.getRelayState(relayNum);
			// Determinar el nuevo valor (invertir el estado actual)
			const newValue = currentState ? 'OFF' : 'ON';

			// Construir el topic: /dingtian/relay<serial>/in/r<number>
			const topic = `/dingtian/relay${this.device_serial}/in/r${relayNum}`;

			// Marcar como procesando
			this.$set(this.togglingRelays, relayKey, true);

			try {
				const api = this.$URL + '/api/v1/mqtt/publish';
				const payload = {
					topic: topic,
					value: newValue
				};

				await this.$axios.post(api, payload);

				// Actualizar el estado localmente (optimistic update)
				if (this.relay_data && this.relay_data.relays && this.relay_data.relays.states) {
					this.$set(this.relay_data.relays.states, relayKey, !currentState);
				}

				// Forzar actualización de datos después de un breve delay
				setTimeout(() => {
					this.fetchRelayInfo();
				}, 500);
			} catch (error) {
				console.error('Error al cambiar estado del relay:', error);
				// Opcional: mostrar notificación de error
				if (this.$EventBus) {
					this.$EventBus.$emit('notifications_modal', {
						type: 'error',
						message: this.$ml.get('error_changing_relay') || 'Error al cambiar el estado del relay'
					});
				}
			} finally {
				// Quitar la marca de procesando
				this.$set(this.togglingRelays, relayKey, false);
			}
		},
		async toggleInput(inputNum) {
			// Validar que tenemos el serial del dispositivo
			if (!this.device_serial) {
				this.updateSerial();
				if (!this.device_serial) {
					return;
				}
			}

			// Evitar múltiples clicks mientras se procesa
			const inputKey = 'input' + inputNum;
			if (this.togglingInputs[inputKey]) {
				return;
			}

			// Verificar que tenemos los datos de INPUTS (no relays)
			if (!this.relay_data || !this.relay_data.inputs || !this.relay_data.inputs.states) {
				console.warn('toggleInput: No hay datos de inputs disponibles');
				this.$set(this.togglingInputs, inputKey, false);
				return;
			}
			
			// Leer el valor crudo ANTES de cualquier procesamiento
			const rawStateValue = this.relay_data.inputs.states[inputKey];
			
			// También verificar si hay un estado previo guardado (del último ciclo de actualización)
			const previousState = this.previousInputStates[inputKey];
			
			// Determinar el estado actual: usar el valor crudo, o el estado previo si el crudo no está disponible
			// Si el valor crudo es explícitamente true, está activo
			// Si es false, null, undefined, o no existe, está inactivo
			// PERO también considerar el estado previo si el valor crudo parece incorrecto
			let currentState = false;
			
			if (rawStateValue === true) {
				currentState = true;
			} else if (rawStateValue === false || rawStateValue === null || rawStateValue === undefined) {
				// Si el valor crudo es false/null/undefined, verificar el estado previo
				// Si hay un estado previo guardado, usarlo como referencia
				if (previousState !== undefined && previousState !== null) {
					currentState = previousState === true;
				} else {
					currentState = false;
				}
			} else {
				// Para otros tipos (string, número), convertir a booleano
				currentState = Boolean(rawStateValue);
			}
			
			console.log('toggleInput - inputNum:', inputNum, 'inputKey:', inputKey);
			console.log('toggleInput - rawStateValue:', rawStateValue, 'type:', typeof rawStateValue);
			console.log('toggleInput - previousState:', previousState);
			console.log('toggleInput - currentState (calculado):', currentState);
			console.log('toggleInput - all states:', JSON.stringify(this.relay_data.inputs.states));
			
			// Determinar el nuevo valor basado en el estado actual
			// Si está ACTIVE (true), el usuario quiere desactivarlo -> enviar OFF
			// Si está INACTIVE (false), el usuario quiere activarlo -> enviar ON
			const newValue = currentState ? 'OFF' : 'ON';
			console.log('toggleInput - newValue a enviar:', newValue, '(basado en estado:', currentState ? 'ACTIVE' : 'INACTIVE', ')');

			// Construir el topic para INPUTS: /dingtian/relay<serial>/in/i<number>
			// Nota: usa 'i' para inputs, no 'r' como en relays
			const topic = `/dingtian/relay${this.device_serial}/in/i${inputNum}`;

			// Debug: mostrar información del estado
			console.log('toggleInput - inputNum:', inputNum, 'inputKey:', inputKey, 'currentState:', currentState, 'newValue:', newValue, 'topic:', topic);
			console.log('toggleInput - relay_data.inputs:', this.relay_data.inputs);
			console.log('toggleInput - relay_data.inputs.states:', this.relay_data.inputs.states);

			// Marcar como procesando
			this.$set(this.togglingInputs, inputKey, true);

			try {
				const api = this.$URL + '/api/v1/mqtt/publish';
				const payload = {
					topic: topic,
					value: newValue
				};

				console.log('Enviando request MQTT:', payload);
				const response = await this.$axios.post(api, payload);
				console.log('Respuesta MQTT:', response.data);

				// Actualizar el estado localmente (optimistic update)
				if (this.relay_data && this.relay_data.inputs && this.relay_data.inputs.states) {
					this.$set(this.relay_data.inputs.states, inputKey, !currentState);
				}

				// Forzar actualización de datos después de un breve delay
				setTimeout(() => {
					this.fetchRelayInfo();
				}, 500);
			} catch (error) {
				console.error('Error al cambiar estado del input:', error);
				// Opcional: mostrar notificación de error
				if (this.$EventBus) {
					this.$EventBus.$emit('notifications_modal', {
						type: 'error',
						message: this.$ml.get('error_changing_input') || 'Error al cambiar el estado del input'
					});
				}
			} finally {
				// Quitar la marca de procesando
				this.$set(this.togglingInputs, inputKey, false);
			}
		},
		startPolling() {
			// Limpiar intervalo anterior si existe
			this.stopPolling();
			// Configurar actualización periódica cada 5 segundos
			this.pollingInterval = setInterval(() => {
				this.fetchRelayInfo();
			}, 5000); // 5000ms = 5 segundos
		},
		stopPolling() {
			if (this.pollingInterval) {
				clearInterval(this.pollingInterval);
				this.pollingInterval = null;
			}
		}
	}
}
</script>

